import { v4 as uuid } from 'uuid';
import React from 'react';
import { PaletteOptions } from '@mui/material';

import {
  DrinkIcon,
  FoodIcon,
  PrizeIcon,
  PumpIcon,
  SelectData,
  SpinWheelGameIcon,
} from '@occc-app/design-system';
import { Random_Award_Type } from '@occc/graphql';

import { GameType, NativeGamePaletteVariant, SegmentFormValues, SegmentIcon } from '../types';
import { OfferTypeSelectProps } from '../../CampaignComponents/components/OfferTypeSelect/OfferTypeSelect';

export const gameTypeSelectItems: OfferTypeSelectProps['items'] = [
  {
    type: GameType.SPIN_THE_WHEEL,
    title: 'Spin to Win Wheel',
    description: 'Members can spin a wheel for a chance to win a prize',
    icon: SpinWheelGameIcon,
  },
];

export const segmentTypeSelectItems: SelectData[] = [
  {
    label: 'Points',
    value: Random_Award_Type.Points,
  },
  {
    label: 'Award',
    value: Random_Award_Type.Vouchers,
  },
  {
    label: 'Try Again',
    value: Random_Award_Type.TryAgain,
  },
  {
    label: 'No Award',
    value: Random_Award_Type.NoAward,
  },
];

// TODO replace with custom icons
export const segmentIconSelectItems: {
  label: string;
  icon: React.ReactNode;
  value: SegmentIcon;
}[] = [
  {
    label: 'Prize',
    value: SegmentIcon.PRIZE,
    icon: <PrizeIcon fontSize='small' />,
  },
  {
    label: 'Pump',
    value: SegmentIcon.PUMP,
    icon: <PumpIcon fontSize='small' />,
  },
  {
    label: 'Food',
    value: SegmentIcon.FOOD,
    icon: <FoodIcon fontSize='small' />,
  },
  {
    label: 'Drink',
    value: SegmentIcon.DRINK,
    icon: <DrinkIcon fontSize='small' />,
  },
];

export const INITIAL_SEGMENTS_LENGTH = 3;
export const MIN_SEGMENTS_LENGTH = 3;
export const NO_AWARD_SEGMENT_ERROR = 'At least one segment must have an award';

export const getDefaultSegment = (): SegmentFormValues => ({
  id: uuid(),
  type: Random_Award_Type.NoAward,
  limit: 1,
  color: null,
  icon: null,
  awardPoints: null,
  awardOffer: null,
  awardOfferExpirationDays: null,
  awardOfferVoucherCount: 1,
});

export const getDefaultSegments = (length: number): SegmentFormValues[] =>
  new Array(length).fill(null).map(() => getDefaultSegment());

const racewayNativeGamePalette = {
  SpinWheel: {
    sectorColors: [
      { color: '#034EA2', contrastText: '#FFFFFF' },
      { color: '#A7D1FF', contrastText: '#034EA2' },
      { color: '#217CE3', contrastText: '#FFFFFF' },
      { color: '#CFDFFB', contrastText: '#034EA2' },
    ],
    rimLightColor: '#FFDA56',
    rimNormalColor: '#FEC807',
    rimDarkColor: '#E89800',
    rimShadowColor: 'rgba(0, 0, 0, 0.2)',
    risingSunBackColor: '#F1F6FF',
    risingSunRaysColor: '#E8F0FF',
    risingSunTextColor: '#034EA2',
    buttonColor: {
      color: '#034EA2',
      contrastText: '#FFFFFF',
    },
  },
};

const cocoNativeGamePalette: PaletteOptions = {
  SpinWheel: {
    sectorColors: [
      { color: '#1E4B7F', contrastText: '#FFFFFF' },
      { color: '#67BDD9', contrastText: '#000000' },
      { color: '#2F6AAD', contrastText: '#FFFFFF' },
      { color: '#C4E5E3', contrastText: '#000000' },
    ],
    rimLightColor: '#FFDA56',
    rimNormalColor: '#FEC807',
    rimDarkColor: '#E89800',
    rimShadowColor: 'rgba(0, 0, 0, 0.2)',
    risingSunBackColor: '#30386C',
    risingSunRaysColor: '#3D4578',
    risingSunTextColor: '#ffffff',
    buttonColor: {
      color: '#FFFFFF',
      contrastText: '#30386C',
    },
  },
};

export const nativeGamePalettes: Record<NativeGamePaletteVariant, PaletteOptions> = {
  coco: cocoNativeGamePalette,
  cefco: {
    SpinWheel: {
      sectorColors: [
        { color: '#EB0029', contrastText: '#FFFFFF' },
        { color: '#FFBECA', contrastText: '#000000' },
        { color: '#FFFFFF', contrastText: '#000000' },
        { color: '#FF8FA2', contrastText: '#000000' },
      ],
      rimLightColor: '#FFDA56',
      rimNormalColor: '#FEC807',
      rimDarkColor: '#E89800',
      rimShadowColor: 'rgba(0, 0, 0, 0.2)',
      risingSunBackColor: '#B2001F',
      risingSunRaysColor: '#BF1B37',
      risingSunTextColor: '#ffffff',
      buttonColor: {
        color: '#FFFFFF',
        contrastText: '#EB0029',
      },
    },
  },
  delek: {
    SpinWheel: {
      sectorColors: [
        { color: '#FAAD17', contrastText: '#000000' },
        { color: '#FFE2AB', contrastText: '#000000' },
        { color: '#FFCA63', contrastText: '#000000' },
        { color: '#FFF5E3', contrastText: '#000000' },
      ],
      rimLightColor: '#FFDA56',
      rimNormalColor: '#FEC807',
      rimDarkColor: '#E89800',
      rimShadowColor: 'rgba(0, 0, 0, 0.2)',
      risingSunBackColor: '#2D2D2D',
      risingSunRaysColor: '#3C3C3C',
      risingSunTextColor: '#FFFFFF',
      buttonColor: {
        color: '#FFFFFF',
        contrastText: '#000000',
      },
    },
  },
  mpc: {
    SpinWheel: {
      sectorColors: [
        { color: '#01458D', contrastText: '#FFFFFF' },
        { color: '#AADCFF', contrastText: '#002D5D' },
        { color: '#1074BD', contrastText: '#EEEEEE' },
        { color: '#76C7FF', contrastText: '#002D5D' },
      ],
      rimLightColor: '#FFDA56',
      rimNormalColor: '#FEC807',
      rimDarkColor: '#E89800',
      rimShadowColor: 'rgba(0, 0, 0, 0.2)',
      risingSunBackColor: '#F3F3F3',
      risingSunRaysColor: '#e5e8eb',
      risingSunTextColor: '#002D5D',
      buttonColor: {
        color: '#002D5D',
        contrastText: '#FFFFFF',
      },
    },
  },
  gulf: {
    SpinWheel: {
      sectorColors: [
        { color: '#082973', contrastText: '#FFFFFF' },
        { color: '#BED9EA', contrastText: '#082973' },
        { color: '#FF6319', contrastText: '#FFFFFF' },
        { color: '#92C3FD', contrastText: '#082973' },
      ],
      rimLightColor: '#FFDA56',
      rimNormalColor: '#FEC807',
      rimDarkColor: '#E89800',
      rimShadowColor: 'rgba(0, 0, 0, 0.2)',
      risingSunBackColor: '#C0E6FF',
      risingSunRaysColor: '#B7DDF8',
      risingSunTextColor: '#082973',
      buttonColor: {
        color: '#082973',
        contrastText: '#FFFFFF',
      },
    },
  },
  yesway: {
    SpinWheel: {
      sectorColors: [
        { color: '#005F62', contrastText: '#FFFFFF' },
        { color: '#FDB813', contrastText: '#005F62' },
        { color: '#078093', contrastText: '#EEEEEE' },
        { color: '#2BB04A', contrastText: '#FFFFFF' },
      ],
      rimLightColor: '#FFDA56',
      rimNormalColor: '#FEC807',
      rimDarkColor: '#E89800',
      rimShadowColor: 'rgba(0, 0, 0, 0.2)',
      risingSunBackColor: '#078093',
      risingSunRaysColor: '#18899B',
      risingSunTextColor: '#FFFFFF',
      buttonColor: {
        color: '#FDB813',
        contrastText: '#005F62',
      },
    },
  },
  raceway: racewayNativeGamePalette,
  gkraceway: racewayNativeGamePalette,
  egamerica: cocoNativeGamePalette,
  stinker: cocoNativeGamePalette,
  rofo: cocoNativeGamePalette,
  jacksons: cocoNativeGamePalette,
  refuel: cocoNativeGamePalette,
  parkers: cocoNativeGamePalette,
};
